import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useSkillCatalog } from "@/hooks/use-skills";
import { type FacetCounts, facetCounts, filterSkills } from "@/lib/skills/catalog";
import { RISK } from "@/lib/skills/constants";
import type { DiscoverySearch } from "@/lib/skills/search-params";
import type { SortDir } from "@/lib/skills/sort";
import type { Category, RiskLevel, SkillListItem } from "@/lib/skills/types";

export interface ActiveFilterPill {
  key: string;
  label: string;
  onClear: () => void;
}

export interface SkillFilterState {
  q: string;
  cat: Category | null;
  risks: RiskLevel[];
  minRating: number;
  freshness: number;
  authors: string[];
  sort: NonNullable<DiscoverySearch["sort"]>;
  dir: SortDir;
  view: "table" | "grid";
}

function normalize(search: DiscoverySearch): SkillFilterState {
  return {
    q: search.q ?? "",
    cat: search.cat ?? null,
    risks: search.risk ?? [],
    minRating: search.minRating ?? 0,
    freshness: search.freshness ?? 0,
    authors: search.authors ?? [],
    sort: search.sort ?? "relevance",
    dir: search.dir ?? "desc",
    view: search.view ?? "table",
  };
}

/**
 * Single source of truth for filtering the skill catalog, bound to the `/skills`
 * route search params. Both the All Skills grid and table consume this so they
 * can never drift. Returns the filtered (unsorted) set; sort is applied by the
 * consumer via `@/lib/skills/sort` (grid) or TanStack Table (table).
 */
export function useSkillFilters(search: DiscoverySearch) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useSkillCatalog();
  const state = normalize(search);

  const update = (patch: Partial<DiscoverySearch>) =>
    void navigate({ to: "/skills", search: (prev) => ({ ...prev, ...patch }) });

  const base = useMemo<SkillListItem[]>(() => data ?? [], [data]);
  const counts: FacetCounts = useMemo(() => facetCounts(base), [base]);

  const filtered = useMemo(
    () =>
      filterSkills(base, {
        search: state.q || undefined,
        categories: state.cat ? [state.cat] : undefined,
        risks: state.risks,
        minRating: state.minRating || undefined,
        freshnessDays: state.freshness || undefined,
        authors: state.authors,
      }),
    [base, state.q, state.cat, state.risks, state.minRating, state.freshness, state.authors],
  );

  // Active-filter pills — everything except category (which is the tab row).
  const activePills: ActiveFilterPill[] = [];
  for (const r of state.risks) {
    activePills.push({
      key: `risk-${r}`,
      label: RISK[r].label,
      onClear: () => update({ risk: state.risks.filter((x) => x !== r) }),
    });
  }
  if (state.minRating) {
    activePills.push({
      key: "rating",
      label: `${state.minRating}★+`,
      onClear: () => update({ minRating: undefined }),
    });
  }
  if (state.freshness) {
    activePills.push({
      key: "fresh",
      label: `Updated ≤ ${state.freshness}d`,
      onClear: () => update({ freshness: undefined }),
    });
  }
  for (const a of state.authors) {
    activePills.push({
      key: `author-${a}`,
      label: counts.authors.find((x) => x.id === a)?.name ?? a,
      onClear: () => update({ authors: state.authors.filter((x) => x !== a) }),
    });
  }

  const clearAll = () =>
    update({
      q: undefined,
      risk: undefined,
      minRating: undefined,
      freshness: undefined,
      authors: undefined,
    });

  return {
    base,
    filtered,
    counts,
    state,
    update,
    activePills,
    activeFilterCount: activePills.length,
    clearAll,
    isLoading,
    isError,
    refetch,
  };
}
